import React, { useState, useCallback, useRef, useEffect } from 'react';
import { graphBuilderApi } from '../../services/api';
import { buildPipelineRunPayload } from '../../utils/pipelineRunPayload';
import TestGraphChat from '../UserHome/TestGraphChat';
import './StarkBenchmark.css';

const GRAPH_ID = 'bf1a60c4-4488-4f89-a630-ec5ea99a823d';
const WORKSPACE_ID = 'c4b164a9-8181-4ae3-9110-034fbf04e145';

// Exact replica of tools/stark_benchmark/run_stark_benchmark.py build_request()
// with demo-only additions: stream_response, include_execution_trace, use_cache,
// expand_with_vocabulary (VQE)
const STARK_SETTINGS = {
  snn_execution_mode: 'compiled_full_numpy',
  min_similarity: 0.40,
  similarity_top_k: 200,
  decompose_query: true,
  expand_with_vocabulary: true,
  enable_abstract_query_handler: true,
  max_gap_fill_iterations: 3,
  graph_traversal_depth: 3,
  enable_cross_encoder_rerank: true,
  context_potency_mode: 'auto',
  auto_potency_boost_multiplier: 1.5,
  compact_render_mode: false,
  enable_context_distillation: false,
  include_verbatim_nodes: false,
  verbatim_inclusion_mode: 'force_exclude',
  enable_judge: true,
  llm_model: 'gpt-4o',
  stage_models: {
    classifier: 'gpt-4o',
    conversational: 'gpt-4o',
    decomposition: 'gpt-4o',
    aqh: 'gpt-4o',
    distillation: 'gpt-4o',
    answer_render: 'gpt-4o',
    judge: 'gpt-4o',
  },
  enable_ser: true,
  ser_temperature: 0.25,
  crs_enabled: true,
  crs_semantic_weight: 0.30,
  crs_node_type_compat_weight: 0.20,
  crs_intent_affinity_weight: 0.15,
  crs_scope_intersection_weight: 0.10,
  crs_causal_modality_weight: 0.10,
  crs_authority_weight: 0.05,
  crs_gate_readiness_weight: 0.05,
  crs_exception_alignment_weight: 0.05,
  crs_evidence_threshold: 0.25,
  crs_default_unscored_relevance: 0.20,
  mlcf_enabled: true,
  mlcf_identity_threshold: 0.40,
  mlcf_scope_overlap_threshold: 0.20,
  mlcf_context_strictness: 'permissive',
  mlcf_min_survivors: 3,
  simulate_duration_ms: 400,
  simulate_step_ms: 10,
  max_recursive_ticks: 40,
  bias_hz: 5,
  gain: 8,
  min_background_rate_hz: 0,
  rate_coding_min_hz: 5,
  rate_coding_max_hz: 200,
  rate_coding_base_hz: 30,
  softmax_temperature: 0.10,
  propagation_evidence_threshold: 0.10,
  potency_weights: {
    rate: 0.10,
    evidence: 0.18,
    spikes: 0.05,
    query_rel: 0.20,
    prop: 0.05,
    gate: 0.05,
    seed_affinity: 0.30,
    excitation_lineage: 0.07,
  },
  // Demo-only additions (not in benchmark runner):
  include_execution_trace: true,
  stream_response: false,
  use_cache: true,
};

// Key settings to show in the Pipeline Configuration panel
const PIPELINE_DISPLAY_SETTINGS = [
  { label: 'SNN Execution Mode', value: STARK_SETTINGS.snn_execution_mode },
  { label: 'LLM Model', value: STARK_SETTINGS.llm_model },
  { label: 'SER Enabled', value: 'true' },
  { label: 'SER Temperature', value: String(STARK_SETTINGS.ser_temperature) },
  { label: 'Similarity Top-K', value: String(STARK_SETTINGS.similarity_top_k) },
  { label: 'Min Similarity', value: String(STARK_SETTINGS.min_similarity) },
  { label: 'Simulation Duration', value: `${STARK_SETTINGS.simulate_duration_ms}ms (${STARK_SETTINGS.max_recursive_ticks} ticks)` },
  { label: 'Decompose Query', value: 'true' },
  { label: 'VQE (Vocab Expansion)', value: 'true' },
  { label: 'Cross-Encoder Rerank', value: 'true' },
  { label: 'Context Potency', value: `auto (boost ${STARK_SETTINGS.auto_potency_boost_multiplier}x)` },
  { label: 'CRS Enabled', value: 'true (biomedical weights)' },
  { label: 'MLCF', value: `permissive (survivors ${STARK_SETTINGS.mlcf_min_survivors})` },
  { label: 'Gap Fill Iterations', value: String(STARK_SETTINGS.max_gap_fill_iterations) },
  { label: 'Graph Traversal Depth', value: String(STARK_SETTINGS.graph_traversal_depth) },
  { label: 'Judge Enabled', value: 'true' },
  { label: 'Streaming', value: 'false (matches benchmark runner)' },
  { label: 'Use Cache (NPZ)', value: 'true' },
];

const BENCHMARK_QUESTIONS = [
  {
    id: 4,
    domain: 'Gene / Protein',
    query: 'Can you supply a compilation of genes and proteins associated with endothelin B receptor interaction, involved in G alpha (q) signaling, and contributing to hypertension and ovulation-related biological functions?',
    goldAnswers: ['EDN1', 'EDN2'],
    expectedTop1: 'EDN1',
  },
  {
    id: 9,
    domain: 'Gene / Protein',
    query: 'Which gene or protein is engaged in DCC-mediated attractive signaling, can bind to actin filaments, and belongs to the actin-binding LIM protein family?',
    goldAnswers: ['ABLIM3', 'ABLIM2', 'ABLIM1'],
    expectedTop1: 'ABLIM3',
  },
  {
    id: 22,
    domain: 'Gene / Protein',
    query: 'Please find the gene or protein participating in IL-27 signaling as a component of the IL-27 complex, involved in inflammation, and responsible for producing the secretory glycoprotein that pairs with a 28-kD protein to form IL-27.',
    goldAnswers: ['STAT3', 'IL27', 'EBI3'],
    expectedTop1: 'IL27',
  },
  {
    id: 25,
    domain: 'Gene / Protein',
    query: "I need to find a gene or protein involved in the cell's response to erythropoietin by interacting with it and also inhibits the SCF/KIT pathway. It must be able to attach to the erythropoietin receptor and help in promoting red blood cell development. Can you give me a list of potential candidates fitting this description?",
    goldAnswers: ['RHEX'],
    expectedTop1: 'RHEX',
  },
  {
    id: 32,
    domain: 'Gene / Protein',
    query: "Could you help me find a gene or protein that is involved in the mRNA deadenylation process, specifically one that binds with CNOT1 to facilitate CNOT10's connection to the CCR4-NOT complex? This gene or protein should also be involved in mRNA degradation and the inhibition of protein synthesis, and is a core element of the CCR4-NOT complex.",
    goldAnswers: ['CNOT10', 'TNKS1BP1', 'CNOT11', 'CNOT9'],
    expectedTop1: 'CNOT10',
  },
  {
    id: 33,
    domain: 'Gene / Protein',
    query: "Identify genes or proteins that interact with the 'SLIT-ROBO expression regulation' pathway, are linked to Diamond-Blackfan anemia, and promote drug resistance in gastric cancer cell lines.",
    goldAnswers: ['RPL23'],
    expectedTop1: 'RPL23',
  },
  {
    id: 39,
    domain: 'Drug',
    query: 'Which drugs targeting the dopamine transporter encoded by SLC6A3 gene are approved for managing Major Depressive Disorder, Generalized Anxiety Disorder, neuropathic pain, osteoarthritis, and stress urinary incontinence?',
    goldAnswers: ['Duloxetine', 'Sertraline', 'Venlafaxine'],
    expectedTop1: 'Duloxetine',
  },
  {
    id: 42,
    domain: 'Pathway',
    query: 'Can you find a signaling pathway that interacts with RAD51, represses E2F-driven gene transcription affecting the G1 to S phase transition, and also downregulates DNA repair genes like RAD51 and RRM2 by binding to E2F6?',
    goldAnswers: ['Transcriptional Regulation by E2F6'],
    expectedTop1: 'Transcriptional Regulation by E2F6',
  },
  {
    id: 57,
    domain: 'Pathway',
    query: 'Please find a pathway illustrating the interplay of FGF6 and GAB2 gene/proteins, which also aligns as a hierarchical subset or superset with the PI3K/AKT Cancer Signaling pathway.',
    goldAnswers: ['Constitutive Signaling by Aberrant PI3K in Cancer'],
    expectedTop1: 'Constitutive Signaling by Aberrant PI3K in Cancer',
  },
  {
    id: 79,
    domain: 'Drug',
    query: "Hello, I'm dealing with ongoing nerve pain and seeking effective treatment options. Could you suggest medications compatible with 4-Methoxyamphetamine for enhanced relief? Additionally, I have an irregular EKG\u2014what drugs should I avoid to prevent further heart complications?",
    goldAnswers: ['Ziconotide', 'Pregabalin'],
    expectedTop1: 'Pregabalin',
  },
];

function extractTopNodeName(topOutcomeNodes) {
  if (!Array.isArray(topOutcomeNodes) || topOutcomeNodes.length === 0) return null;
  const first = topOutcomeNodes[0];
  if (typeof first !== 'string') return null;
  const dashIdx = first.indexOf(' - ');
  return dashIdx >= 0 ? first.substring(dashIdx + 3) : first;
}

function isHit(systemNode, goldAnswers) {
  if (!systemNode || !goldAnswers) return false;
  const sysLower = systemNode.toLowerCase().trim();
  return goldAnswers.some(g => g.toLowerCase().trim() === sysLower);
}

const StarkBenchmarkPage = () => {
  const [messages, setMessages] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [results, setResults] = useState({});
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [pipelineExpanded, setPipelineExpanded] = useState(false);
  const [cacheStatus, setCacheStatus] = useState(null);
  const [cacheLoading, setCacheLoading] = useState(true);
  const chatRef = useRef(null);
  const activeQuestionRef = useRef(null);

  // Keep ref in sync so the onComplete callback always sees the latest value
  useEffect(() => { activeQuestionRef.current = activeQuestionId; }, [activeQuestionId]);

  // Check SER/NPZ cache status on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await graphBuilderApi.getCacheStatus(GRAPH_ID);
        if (!cancelled) setCacheStatus(status);
      } catch (err) {
        console.warn('Cache status check failed:', err);
        if (!cancelled) setCacheStatus({ error: true });
      } finally {
        if (!cancelled) setCacheLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Click a question row -> fill the chat input (don't auto-submit)
  const handleQuestionClick = useCallback((q) => {
    setActiveQuestionId(q.id);
    if (chatRef.current) {
      chatRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    window.postMessage({ type: 'cm:fill-input', text: q.query }, '*');
  }, []);

  const handleSendMessage = useCallback(async (userQuery) => {
    const userMsg = {
      id: Date.now(),
      type: 'user',
      content: userQuery,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);

    const loadingId = Date.now() + 1;
    setMessages(prev => [...prev, {
      id: loadingId,
      type: 'streaming',
      content: '',
      phase: 'Running pipeline (non-streaming)...',
      timestamp: new Date().toISOString(),
    }]);

    const payload = buildPipelineRunPayload({
      settings: STARK_SETTINGS,
      nl_query: userQuery,
      graph_id: GRAPH_ID,
      workspace_id: WORKSPACE_ID,
      conversation_history: conversationHistory,
    });

    try {
      const data = await graphBuilderApi.runPipeline(payload);
      const finalContent = data.natural_language_answer || 'No response received.';
      const topNodes = data.top_outcome_nodes || data.top_node_ids || [];
      const topNodeName = extractTopNodeName(topNodes);
      const insightId = data.insight_id || null;

      setMessages(prev => prev.map(m =>
        m.id === loadingId
          ? {
              ...m,
              type: 'assistant',
              content: finalContent,
              phase: undefined,
              metadata: {
                score: data.score,
                top_outcome_nodes: topNodes,
                execution_trace: data.execution_trace || null,
                citation_index: data.citation_index || data.execution_trace?.citation_index || null,
                insightId,
              },
            }
          : m
      ));

      setConversationHistory(prev => [
        ...prev,
        { role: 'user', content: userQuery },
        { role: 'assistant', content: finalContent, nodes: topNodes },
      ]);

      const currentQId = activeQuestionRef.current;
      if (currentQId && topNodeName) {
        setResults(prev => ({
          ...prev,
          [currentQId]: {
            systemTop1: topNodeName,
            insightId,
            score: data.score,
          },
        }));
      }
    } catch (err) {
      setMessages(prev => prev.map(m =>
        m.id === loadingId
          ? { ...m, type: 'error', content: err?.message || 'Pipeline error', phase: undefined }
          : m
      ));
    }
  }, [conversationHistory]);

  // Open insight in new tab (don't navigate away from demo)
  const handleViewInsight = useCallback((insightId) => {
    if (insightId) {
      window.open(
        `/user-home/graphs/build/${GRAPH_ID}/build-tune/explainability?insight=${insightId}`,
        '_blank'
      );
    }
  }, []);

  const handleOpenTestGraph = useCallback(() => {
    window.open(
      `/user-home/graphs/build/${GRAPH_ID}/train-test/test-graph`,
      '_blank'
    );
  }, []);

  const completedCount = BENCHMARK_QUESTIONS.filter(q => {
    const r = results[q.id];
    return r && isHit(r.systemTop1, q.goldAnswers);
  }).length;

  // Derive cache status display
  const cacheCompiled = cacheStatus?.layers?.compile?.warm || cacheStatus?.compiled || false;
  const cacheSer = cacheStatus?.layers?.ser?.warm || cacheStatus?.has_ser_receptors || false;

  return (
    <div className="stark-benchmark-page">
      {/* Hero Section */}
      <div className="stark-hero">
        <div className="stark-hero-badge">Stanford STaRK-Prime Benchmark</div>
        <h1 className="stark-hero-title">
          100% Hit@1 {'\u2014'} Perfect Score
        </h1>
        <p className="stark-hero-subtitle">
          CerebralMachines achieved a perfect Hit@1 on the Stanford STaRK-Prime biomedical knowledge graph
          benchmark — every gold answer ranked #1 against 100-200+ curated near-miss distractors per query.
        </p>

        <div className="stark-stats-row">
          <div className="stark-stat">
            <span className="stark-stat-value">10/10</span>
            <span className="stark-stat-label">Hit@1</span>
          </div>
          <div className="stark-stat">
            <span className="stark-stat-value">1.0</span>
            <span className="stark-stat-label">MRR</span>
          </div>
          <div className="stark-stat">
            <span className="stark-stat-value">238K+</span>
            <span className="stark-stat-label">Entities</span>
          </div>
          <div className="stark-stat">
            <span className="stark-stat-value">18</span>
            <span className="stark-stat-label">Relation Types</span>
          </div>
          <div className="stark-stat">
            <span className="stark-stat-value">5-9s</span>
            <span className="stark-stat-label">Avg Latency</span>
          </div>
        </div>

        <div className="stark-hero-buttons">
          <button
            className="stark-about-toggle"
            onClick={() => setAboutExpanded(!aboutExpanded)}
          >
            {aboutExpanded ? 'Hide details' : 'About this benchmark'}
          </button>
          <button
            className="stark-about-toggle"
            onClick={() => setPipelineExpanded(!pipelineExpanded)}
          >
            {pipelineExpanded ? 'Hide pipeline config' : 'Pipeline configuration'}
          </button>
          <button
            className="stark-about-toggle stark-testgraph-btn"
            onClick={handleOpenTestGraph}
          >
            Open Test Graph
          </button>
        </div>

        {aboutExpanded && (
          <div className="stark-about-content">
            <h3>What is STaRK?</h3>
            <p>
              STaRK (Structured and Textual Aligned Retrieval over Knowledge graphs) is a benchmark developed
              by Stanford University researchers, published at NeurIPS 2024. It evaluates how well systems
              can retrieve specific entities from large, real-world knowledge graphs when given complex
              natural language queries.
            </p>
            <h3>Why is STaRK-Prime so difficult?</h3>
            <p>
              Unlike simple keyword retrieval, STaRK queries require <strong>multi-hop reasoning</strong> across
              heterogeneous biological relationships (protein interactions, drug indications, pathway membership,
              gene expression). Each query comes with 100-200+ <strong>curated near-miss distractors</strong> -
              entities semantically similar to the correct answer but that do not satisfy all constraints.
              Published baselines from embedding-only and GNN approaches typically achieve 40-65% Hit@1.
            </p>
            <h3>What does this demo show?</h3>
            <p>
              Below are the 10 benchmark questions. Click any question to populate the chat input,
              then press Send to execute the full CerebralMachines SNN pipeline in real time.
              The results table confirms whether the system's #1-ranked node matches the
              Stanford gold answer - verifying the Hit@1 claim live.
            </p>
          </div>
        )}

        {pipelineExpanded && (
          <div className="stark-about-content stark-pipeline-config">
            <h3>Pipeline Execution Parameters</h3>
            <p>These are the exact settings used in the benchmark evaluation (from <code>run_stark_benchmark.py</code>).</p>
            <div className="stark-config-grid">
              {PIPELINE_DISPLAY_SETTINGS.map(s => (
                <div key={s.label} className="stark-config-item">
                  <span className="stark-config-label">{s.label}</span>
                  <span className="stark-config-value">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cache Status */}
      <div className="stark-cache-status">
        <span className="stark-cache-label">Graph Cache:</span>
        {cacheLoading ? (
          <span className="stark-cache-value stark-cache-loading">Checking...</span>
        ) : cacheStatus?.error ? (
          <span className="stark-cache-value stark-cache-warn">Unable to check cache status</span>
        ) : (
          <>
            <span className={`stark-cache-dot ${cacheCompiled ? 'stark-cache-ok' : 'stark-cache-warn'}`} />
            <span className="stark-cache-value">
              NPZ: {cacheCompiled ? 'Cached' : 'Not cached'}
            </span>
            <span className={`stark-cache-dot ${cacheSer ? 'stark-cache-ok' : 'stark-cache-warn'}`} />
            <span className="stark-cache-value">
              SER: {cacheSer ? 'Compiled' : 'Not compiled'}
            </span>
            {(!cacheCompiled || !cacheSer) && (
              <span className="stark-cache-warning">
                Cache may need warming - first query may be slower.
              </span>
            )}
          </>
        )}
      </div>

      {/* Live Verification Counter */}
      <div className="stark-live-counter">
        <span className="stark-live-label">Live Verification:</span>
        <span className="stark-live-value">{completedCount} / 10 confirmed</span>
        <div className="stark-live-bar">
          <div
            className="stark-live-bar-fill"
            style={{ width: `${(completedCount / 10) * 100}%` }}
          />
        </div>
      </div>

      {/* Chat Panel */}
      <div className="stark-chat-section" ref={chatRef}>
        <h2 className="stark-section-title">Live Pipeline Execution</h2>
        <p className="stark-section-desc">
          Click a question below to populate the chat, then press Send to execute the full SNN pipeline.
        </p>
        <div className="stark-chat-wrapper">
          <TestGraphChat
            graphId={GRAPH_ID}
            messages={messages}
            onSendMessage={handleSendMessage}
            conversationHistory={conversationHistory}
            onViewInsight={handleViewInsight}
            welcomeMessage="Select a benchmark question below, or type your own query against the STaRK-Prime biomedical knowledge graph."
            suggestedQuestions={[
              BENCHMARK_QUESTIONS[0].query.substring(0, 80) + '...',
              BENCHMARK_QUESTIONS[6].query.substring(0, 80) + '...',
              BENCHMARK_QUESTIONS[7].query.substring(0, 80) + '...',
            ]}
          />
        </div>
      </div>

      {/* Benchmark Questions Table */}
      <div className="stark-table-section">
        <h2 className="stark-section-title">Benchmark Questions & Results</h2>
        <div className="stark-table-container">
          <table className="stark-table">
            <thead>
              <tr>
                <th className="stark-th-id">Q#</th>
                <th className="stark-th-domain">Domain</th>
                <th className="stark-th-query">Query</th>
                <th className="stark-th-gold">Gold Answer(s)</th>
                <th className="stark-th-expected">Expected #1</th>
                <th className="stark-th-system">System #1</th>
                <th className="stark-th-status">Hit@1</th>
              </tr>
            </thead>
            <tbody>
              {BENCHMARK_QUESTIONS.map(q => {
                const r = results[q.id];
                const hit = r ? isHit(r.systemTop1, q.goldAnswers) : null;
                const isActive = activeQuestionId === q.id;

                return (
                  <tr
                    key={q.id}
                    className={`stark-row ${isActive ? 'stark-row-active' : ''} ${hit === true ? 'stark-row-hit' : ''}`}
                    onClick={() => handleQuestionClick(q)}
                    title="Click to load this question into the chat"
                  >
                    <td className="stark-td-id">{q.id}</td>
                    <td className="stark-td-domain">
                      <span className={`stark-domain-badge stark-domain-${q.domain.toLowerCase().replace(/\s*\/\s*/g, '-')}`}>
                        {q.domain}
                      </span>
                    </td>
                    <td className="stark-td-query">
                      {q.query.length > 120 ? q.query.substring(0, 120) + '...' : q.query}
                    </td>
                    <td className="stark-td-gold">{q.goldAnswers.join(', ')}</td>
                    <td className="stark-td-expected">{q.expectedTop1}</td>
                    <td className="stark-td-system">
                      {r ? (
                        <span className="stark-system-result">
                          {r.systemTop1}
                          {r.insightId && (
                            <button
                              className="stark-insight-link"
                              onClick={(e) => { e.stopPropagation(); handleViewInsight(r.insightId); }}
                              title="View Execution Insight (opens in new tab)"
                            >
                              View Insight
                            </button>
                          )}
                        </span>
                      ) : (
                        <span className="stark-pending">{'\u2014'}</span>
                      )}
                    </td>
                    <td className="stark-td-status">
                      {hit === true && <span className="stark-hit">{'\u2713'}</span>}
                      {hit === false && <span className="stark-miss">{'\u2717'}</span>}
                      {hit === null && <span className="stark-untested">{'\u00b7'}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="stark-footer">
        <p>
          STaRK-Prime benchmark by Stanford University (Bai et al., NeurIPS 2024).
          Full evaluation details in the{' '}
          <button
            className="stark-link-btn"
            onClick={() => window.open('https://github.com/snap-stanford/stark', '_blank')}
          >
            STaRK GitHub repository
          </button>.
        </p>
      </div>
    </div>
  );
};

export default StarkBenchmarkPage;
